import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Paper,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { updateClass, fetchUsers } from "../../api/backend";
import { useTenant } from "../../contexts/TenantContext";
import HowToGuideBox from "../../components/HowToGuideBox";
import useAhaTeachingCheck from "../../components/certificates/useAhaTeachingCheck";
import { useTheme } from "@mui/material/styles";

const DS = {
  surfaceContainerLow: "var(--color-surface-press-light)",
  surfaceContainerHigh: "var(--color-surface-press-stronger)",
  onSurface: "var(--color-ink)",
  onSurfaceVariant: "rgba(31,22,51,0.7)",
  outlineVariant: "var(--color-hairline-cloud)",
};

function getInstructorDisplayName(instructor) {
  return instructor?.fullName || instructor?.name || instructor?.username || "Unknown Instructor";
}

export default function InstructorsTab({ course, canEdit }) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const { id } = useParams();
  const { user } = useTenant();

  const [leadInstructorId, setLeadInstructorId] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState("success");
  // An AHA class can only be led by an instructor with an AHA instructor ID in this center.
  const aha = useAhaTeachingCheck(id);

  // Initialize from course data
  useEffect(() => {
    setLeadInstructorId(course?.leadInstructorId || null);
  }, [course]);

  // Fetch every instructor/coordinator in the active tenant — any of them can
  // be the lead, or picked up per-session via SessionOpening.
  useEffect(() => {
    const fetchInstructors = async () => {
      setLoading(true);
      try {
        const allUsers = await fetchUsers().catch(() => []);
        if (!Array.isArray(allUsers)) {
          setInstructors([]);
          return;
        }
        const tenantInstructors = allUsers.filter((u) => {
          const tenantRoles = Array.isArray(u.tenantRoles?.[user?.tenantId])
            ? u.tenantRoles[user?.tenantId]
            : [];
          return (
            u.roles?.includes("INSTRUCTOR") ||
            u.roles?.includes("COURSE_COORDINATOR") ||
            tenantRoles.includes("INSTRUCTOR") ||
            tenantRoles.includes("COURSE_COORDINATOR")
          );
        });
        setInstructors(tenantInstructors);
      } finally {
        setLoading(false);
      }
    };
    fetchInstructors();
  }, [user]);

  const handleSave = async () => {
    if (!leadInstructorId) {
      setMessageType("error");
      setMessage("Please select a lead instructor.");
      return;
    }

    setSaving(true);
    try {
      await updateClass(user?.tenantId, id, { leadInstructorId });
      setMessageType("success");
      setMessage("Lead instructor saved successfully!");
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessageType("error");
      setMessage(err?.response?.data?.message || err.message || "Failed to save lead instructor. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} color={tPrimary} sx={{ fontFamily: "var(--font-display)" }}>
          Lead Instructor
        </Typography>
        <HowToGuideBox
          title="How to Assign the Lead Instructor"
          role="Course Coordinators"
          steps={[
            {
              number: 1,
              title: "Select Lead Instructor",
              description: "Choose the primary instructor responsible for the course.",
              details:
                "The lead instructor can create sessions, manage attendance, and sign off on competencies.",
            },
            {
              number: 2,
              title: "Assign per-session instructors",
              description: "Additional instructors are assigned per session, not at the course level.",
              details:
                "Use the Sessions tab to open teaching-role slots (Lead / Assistant / TA) on each session and assign or request instructors there.",
            },
            {
              number: 3,
              title: "Save",
              description: "Click Save to confirm the lead instructor.",
              details: "Once saved, the lead instructor has access to manage this course.",
            },
          ]}
          tips={[
            "A lead instructor must be assigned to create sessions.",
            "Only users with INSTRUCTOR or COURSE_COORDINATOR roles can be assigned.",
            "You can change the lead instructor at any time.",
          ]}
        />
      </Box>

      {!canEdit && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Only Course Coordinators can edit the lead instructor.
        </Alert>
      )}

      {message && (
        <Alert severity={messageType} onClose={() => setMessage(null)} sx={{ mb: 3 }}>
          {message}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: `1px solid ${DS.outlineVariant}`,
          borderRadius: "12px",
          bgcolor: "var(--color-surface-canvas-light)",
        }}
      >
        <FormControl fullWidth size="small">
          <Select
            value={leadInstructorId || ""}
            onChange={(e) => setLeadInstructorId(e.target.value ? Number(e.target.value) : null)}
            disabled={!canEdit}
            displayEmpty
            renderValue={(value) => {
              if (!value) {
                return <span style={{ color: DS.onSurfaceVariant, opacity: 0.7 }}>Select lead instructor</span>;
              }
              const instr = instructors.find((i) => i.id === Number(value));
              return instr ? getInstructorDisplayName(instr) : "Unknown Instructor";
            }}
          >
            <MenuItem value=""><em style={{ color: DS.onSurfaceVariant }}>Select lead instructor</em></MenuItem>
            {instructors.map((instr) => (
              <MenuItem key={instr.id} value={instr.id} disabled={aha.blocked(instr.id)}>
                {getInstructorDisplayName(instr)}
                {aha.blocked(instr.id) && " — no AHA instructor ID"}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 3 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon fontSize="small" />}
            onClick={handleSave}
            disabled={saving || !leadInstructorId || !canEdit}
            sx={{
              bgcolor: tPrimary,
              color: "var(--color-on-primary)",
              fontWeight: 700,
              textTransform: "none",
              fontSize: "0.9rem",
              px: 3,
              borderRadius: "8px",
              boxShadow: "none",
              "&:hover": { bgcolor: theme.palette.primary.dark, boxShadow: "none" },
              "&.Mui-disabled": { bgcolor: DS.surfaceContainerHigh, color: DS.onSurfaceVariant },
            }}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
